/* Replan, as a conversation rather than a button.
 *
 * Pressing Replan wrote a whole day and told you the count afterwards: "Placed
 * four things." That is a strange amount of confidence to have on somebody
 * else's behalf — it is their day, the plan is a proposal, and the one moment
 * they can disagree with it cheaply is *before* it is written.
 *
 * So: propose, show, then apply. And show the part that matters most, which is
 * never the list of things that fitted:
 *
 *   · **what could not be placed**, with the constraint that stopped it;
 *   · **what gave way** — a ritual skipped, work displaced — because a plan
 *     that quietly drops the gym every Tuesday is the plan people stop
 *     trusting, and this is the only moment it can be said out loud.
 *
 * And the proposal can be steered. "Only until five", "just these two", "leave
 * ten minutes between things", "move what you already placed" are the things
 * people say to a planner, and each is one control under Options. Changing
 * one asks again; nothing is written until Put it on the day.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Block, Task } from '@shared/types';
import { get, post, errorMessage } from '../api';
import Dialog from './Dialog';
import Icon from './Icon';
import { friendlyDate, human, toMinuteOfDay } from './time';

interface Proposal {
  proposal?: Block[] | { blocks: Block[] };
  plan?: { blocks: Block[] };
  summary?: { placed?: number; freeMinutes?: number; unusualDay?: string };
  unplaced?: ({ title: string; reason: string } | { task: { title: string }; reason: string })[];
  displaced?: ({ title: string } | string)[];
  skipped?: ({ title: string } | string)[];
  lifted?: { title: string; start: string }[];
}

type Fill = 'light' | 'normal' | 'full';

interface Options {
  from: 'now' | 'start';
  until: string;
  fill: Fill;
  gapMin: number;
  breaks: boolean;
  redo: boolean;
  /** null is every waiting task. */
  skip: string[];
}

const FILLS: { value: Fill; label: string; hint: string }[] = [
  { value: 'light', label: 'Light', hint: 'Under half of your free time' },
  { value: 'normal', label: 'Normal', hint: 'Your usual realism setting' },
  { value: 'full', label: 'Full', hint: 'Nearly all of it' }
];

const titleOf = (x: unknown): string =>
  typeof x === 'string' ? x
    : typeof x === 'object' && x && 'title' in x ? String((x as { title: unknown }).title)
    : typeof x === 'object' && x && 'task' in x
      ? String(((x as { task: { title: unknown } }).task).title)
      : '';

export default function ReplanDialog({ date, today, onClose, onApplied }: {
  date: string;
  /** The real today, so "from now" is only offered where it means something. */
  today?: string;
  onClose: () => void;
  onApplied: (placed: number) => void;
}) {
  const qc = useQueryClient();
  const isToday = !!today && date === today;
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<Options>({
    from: isToday ? 'now' : 'start', until: '', fill: 'normal', gapMin: 0,
    breaks: true, redo: false, skip: []
  });
  const set = <K extends keyof Options>(k: K, v: Options[K]) => setOpts(o => ({ ...o, [k]: v }));

  const tasks = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const r = await get<{ tasks: Task[] }>('/api/tasks');
      return r.ok ? r.tasks : [];
    }
  });
  const waiting = (tasks.data ?? []).filter(t => !t.done && !t.scheduled);

  const body = {
    date,
    from: opts.from,
    until: /^\d{2}:\d{2}$/.test(opts.until) ? toMinuteOfDay(opts.until) : null,
    fill: opts.fill,
    gapMin: opts.gapMin,
    breaks: opts.breaks,
    redo: opts.redo,
    ...(opts.skip.length ? { taskIds: waiting.filter(t => !opts.skip.includes(t.id)).map(t => t.id) } : {})
  };

  /* The proposal is a query on the options: change one and it is asked again,
     with the last answer kept on screen meanwhile so the dialog does not
     flicker empty between two nearly identical plans. */
  const look = useQuery({
    queryKey: ['replan-proposal', body],
    gcTime: 0,
    staleTime: 0,
    placeholderData: prev => prev,
    queryFn: async () => {
      const r = await post<Proposal>('/api/plan/auto', { ...body, apply: false });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not build a plan.'));
      return r as Proposal;
    }
  });

  const apply = useMutation({
    mutationFn: async () => {
      const r = await post<Proposal>('/api/plan/auto', { ...body, apply: true });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return r;
    },
    onSuccess: r => {
      void qc.invalidateQueries();
      onApplied(r.summary?.placed ?? 0);
      onClose();
    }
  });

  const seen = look.data;
  const raw = seen?.proposal;
  const blocks: Block[] = Array.isArray(raw) ? raw : raw?.blocks ?? seen?.plan?.blocks ?? [];
  /* New placements have no id yet; what was already on the day does. An
     answer without that distinction falls back to every task block. */
  const fresh = blocks.filter(b => b.taskId && !b.id);
  const placed = fresh.length ? fresh : (seen?.summary?.placed ? blocks.filter(b => b.taskId) : []);
  const unplaced = seen?.unplaced ?? [];
  const gave = [...(seen?.displaced ?? []), ...(seen?.skipped ?? [])].map(titleOf).filter(Boolean);
  const lifted = seen?.lifted ?? [];
  const error = (look.error as Error | null)?.message ?? (apply.error as Error | null)?.message ?? null;

  const changed = opts.from !== (isToday ? 'now' : 'start') || opts.until || opts.fill !== 'normal' ||
    opts.gapMin || !opts.breaks || opts.redo || opts.skip.length;

  return (
    <Dialog
      icon="sparkle"
      title={`Replan ${friendlyDate(date)}`}
      description="Nothing is written until you say so — this is what Amon would do."
      onClose={onClose}>
      <div className="mapout">
        <button type="button" className={`btn btn-ghost btn-sm replan-toggle${changed ? ' is-changed' : ''}`}
                aria-expanded={open} onClick={() => setOpen(o => !o)}>
          <Icon name="filter" size={15} />Options{changed ? ' · changed' : ''}
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
        </button>

        {open && (
          <div className="replan-options">
            {isToday && (
              <fieldset className="repeat-picker">
                <legend>Plan from</legend>
                {(['now', 'start'] as const).map(v => (
                  <label key={v} className={`chip chip-pick${opts.from === v ? ' is-on' : ''}`}>
                    <input type="radio" name="replan-from" checked={opts.from === v}
                           onChange={() => set('from', v)} />
                    <Icon name={v === 'now' ? 'clock' : 'sun'} size={14} />
                    {v === 'now' ? 'Now' : 'Start of the day'}
                  </label>
                ))}
              </fieldset>
            )}

            <div className="field">
              <label htmlFor="replan-until">Nothing after</label>
              <div className="inline">
                <input id="replan-until" type="time" value={opts.until}
                       onChange={e => set('until', e.target.value)} />
                {opts.until && (
                  <button type="button" className="btn btn-ghost btn-icon" aria-label="No limit"
                          title="No limit" onClick={() => set('until', '')}><Icon name="close" size={14} /></button>
                )}
              </div>
            </div>

            <div className="field">
              <label htmlFor="replan-gap">Space after each thing</label>
              <select id="replan-gap" value={opts.gapMin}
                      onChange={e => set('gapMin', Number(e.target.value))}>
                {[0, 5, 10, 15, 30].map(m => <option key={m} value={m}>{m ? `${m} minutes` : 'None'}</option>)}
              </select>
            </div>

            <fieldset className="repeat-picker">
              <legend>How full</legend>
              {FILLS.map(f => (
                <label key={f.value} className={`chip chip-pick${opts.fill === f.value ? ' is-on' : ''}`}
                       title={f.hint}>
                  <input type="radio" name="replan-fill" checked={opts.fill === f.value}
                         onChange={() => set('fill', f.value)} />
                  {f.label}
                </label>
              ))}
            </fieldset>

            <label className="check-field">
              <input type="checkbox" checked={opts.breaks} onChange={e => set('breaks', e.target.checked)} />
              <span><Icon name="coffee" size={14} />Add breaks between long runs</span>
            </label>
            <label className="check-field">
              <input type="checkbox" checked={opts.redo} onChange={e => set('redo', e.target.checked)} />
              <span><Icon name="refresh" size={14} />Rearrange what Amon already placed
                <em className="muted"> — pinned and hand-placed things stay put</em></span>
            </label>

            {waiting.length > 0 && (
              <fieldset className="replan-tasks">
                <legend>What to place</legend>
                {waiting.map(t => (
                  <label key={t.id} className="check-field">
                    <input type="checkbox" checked={!opts.skip.includes(t.id)}
                           onChange={e => set('skip', e.target.checked
                             ? opts.skip.filter(x => x !== t.id) : [...opts.skip, t.id])} />
                    <span>{t.title} <em className="muted">{human(Math.max(5, t.mins - (t.doneMin || 0)))}</em></span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        )}

        {look.isPending && <p className="muted small">Working out the day…</p>}
        {error && <p className="auth-err" role="alert">{error}</p>}

        {seen && (
          <div aria-busy={look.isFetching}>
            <p className="mapout-headline">
              {placed.length === 0
                ? 'Nothing new to place — either the day is full or nothing is waiting.'
                : `${placed.length} thing${placed.length === 1 ? '' : 's'}, ` +
                  `${human(placed.reduce((a, b) => a + (b.endMin - b.startMin), 0))} in all.`}
            </p>

            {seen.summary?.unusualDay && (
              <p className="muted small">{seen.summary.unusualDay}.</p>
            )}

            {lifted.length > 0 && (
              <p className="muted small">
                Taking back {lifted.map(l => `${l.title} (${l.start})`).join(', ')} to place again.
              </p>
            )}

            {placed.length > 0 && (
              <ul className="mapout-days">
                {placed.map((b, i) => (
                  <li key={`${b.startMin}-${i}`}>
                    <strong>{b.start}</strong>
                    <span className="muted small">{b.title}</span>
                  </li>
                ))}
              </ul>
            )}

            {unplaced.length > 0 && (
              <div className="notice notice-bad">
                <p><strong>{unplaced.length} would not fit.</strong></p>
                <ul className="mapout-unplaced">
                  {unplaced.map((u, i) => (
                    <li key={i}>
                      <strong>{titleOf(u)}</strong>
                      <span className="muted small"> · {u.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {gave.length > 0 && (
              <div className="notice">
                <p><strong>What gives way</strong></p>
                <p className="muted small">
                  {gave.join(', ')}
                  {' — '}said here rather than discovered later. A plan that quietly drops
                  the same thing every week is the plan people stop trusting.
                </p>
              </div>
            )}

            <div className="inline wrap">
              <button className="btn btn-primary btn-sm" onClick={() => apply.mutate()}
                      disabled={apply.isPending || look.isFetching}>
                {apply.isPending ? 'Placing…'
                  : placed.length ? `Put ${placed.length} on the day` : 'Rebuild anyway'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Leave it as it is</button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
